import { useCallback, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Inbox } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { usePipelinePresence } from '@/hooks/use-pipeline-presence';
import { usePipelineContext } from '../../context';
import { PipelineCard } from '../PipelineCard';
import { buildCardPresenceMap, makeRegisterCardRef } from '../../utils';
import { useStageFocusRecovery } from '../../hooks';
import type { PipelineStage, Customer, PresenceUser } from '../../types';

// Stable empty array for cards with no presence — frozen to prevent accidental mutation
const EMPTY_PRESENCE = Object.freeze([] as PresenceUser[]) as PresenceUser[];

interface PipelineStageContentProps {
  stage: PipelineStage;
  customers: Customer[];
}

/**
 * Stage content container - displays customer cards or empty state
 * Uses flex layout to fill available height from parent
 * Handles drag-drop for moving customers between stages
 *
 * Reads context ONCE here and threads all needed values as props to PipelineCard,
 * so React.memo on cards can actually prevent re-renders during search
 */
export function PipelineStageContent({ stage, customers }: PipelineStageContentProps) {
  const prefersReducedMotion = useReducedMotion();
  const { toast } = useToast();
  const { state, actions, meta } = usePipelineContext();
  const { presence: pipelinePresence, currentUserId } = usePipelinePresence();

  const { draggedCustomer } = state;
  const { highlightTerms } = meta;
  const searchActive = meta.searchActive;
  const stageMatchCounts = meta.stageMatchCounts;
  const shouldDimStage = searchActive && (stageMatchCounts.get(stage.id) ?? 0) === 0;

  // Track drag over state for visual feedback
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.classList.remove('bg-slate-100', 'dark:bg-slate-800');

      if (draggedCustomer && draggedCustomer.pipelineStageId !== stage.id) {
        // Check if this is a special stage that needs a modal
        if (stage.name === 'Won') {
          actions.openWonModal({
            customerId: draggedCustomer.id,
            customer: draggedCustomer,
            toStageId: stage.id,
            stageName: stage.name,
          });
        } else if (stage.name === 'Installation Completed') {
          actions.openInstallationModal({
            customerId: draggedCustomer.id,
            customer: draggedCustomer,
            toStageId: stage.id,
            stageName: stage.name,
          });
        } else {
          // Normal stage update without modal
          actions.updateCustomer(draggedCustomer.id, {
            pipelineStageId: stage.id,
            pipelineNotes: draggedCustomer.pipelineNotes || null,
          });

          toast({
            title: 'Customer Moved',
            description: `${draggedCustomer.name} moved to ${stage.name}`,
          });
        }
      }

      // Reset dragged customer
      actions.onDragEnd();
    },
    [draggedCustomer, stage, actions, toast]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      meta.handleStageScroll(stage.id, e);
    },
    [meta, stage.id]
  );

  // Pre-build per-card presence map (stable references for React.memo)
  const cardPresenceMap = useMemo(
    () => buildCardPresenceMap(pipelinePresence.cards, currentUserId),
    [pipelinePresence.cards, currentUserId]
  );

  // Stable callback for registering card refs
  const registerRef = useMemo(
    () => makeRegisterCardRef(meta.refs.customerCardRefs),
    [meta.refs]
  );

  // Focus recovery: when a card that had focus is removed (filter/move/collab
  // update), browser drops focus to body. Hand it back to the stage container
  // so keyboard users don't lose their place. Hook handles scoping, identity
  // tracking, and the stage-fighting edge case.
  const stageContainerId = `stage-panel-${stage.id}`;
  useStageFocusRecovery(customers, stageContainerId);

  return (
    <div
      id={stageContainerId}
      ref={meta.getStageContainerRef(stage.id)}
      tabIndex={-1}
      aria-label={`${stage.name} deals list`}
      className={`stage-content-modern h-full p-3 focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-inset ${
        shouldDimStage ? 'opacity-60' : ''
      } ${isDragOver ? 'stage-drop-active' : ''}`}
      style={{
        // Fill available height from parent flexbox
        overflowY: 'auto',
        // Defensive: browser-native scroll anchoring is the default, but set
        // explicitly to document intent and guard against an ancestor
        // overriding it to 'none'. Actual fix for scroll jumping was the
        // removal of AnimatePresence popLayout above this component.
        overflowAnchor: 'auto',
        scrollPaddingTop: '8px',
        scrollPaddingBottom: '8px',
        scrollbarWidth: 'thin',
        transform: 'translateZ(0)',
      }}
      onScroll={handleScroll}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => {
        setIsDragOver(false);
        handleDrop(e);
      }}
    >
      {customers.length === 0 ? (
        <motion.div
          className="stage-empty-state"
          initial={prefersReducedMotion ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Inbox className="stage-empty-icon" />
          <p className="text-sm font-medium">
            {highlightTerms.length > 0
              ? 'No matching deals'
              : 'No deals yet'}
          </p>
          <p className="text-xs">Drag a deal here</p>
        </motion.div>
      ) : (
        <div className="space-y-2.5" role="list" aria-label={`${stage.name} deals`}>
          {customers.map((customer) => {
            const isSearchMatch = meta.matchingCustomerIds.has(customer.id);

            return (
              <PipelineCard
                key={customer.id}
                customer={customer}
                stage={stage}
                isSelected={state.selectedCustomerId === customer.id}
                isHighlighted={state.highlightedCustomerId === customer.id}
                isSearchMatch={isSearchMatch}
                searchActive={searchActive}
                dimCard={searchActive && !state.focusMatchesOnly && !isSearchMatch}
                isFiltering={state.isFiltering}
                highlightTerms={highlightTerms}
                isCopied={meta.isCopied}
                cardPresence={cardPresenceMap.get(customer.id) ?? EMPTY_PRESENCE}
                copyToClipboard={meta.copyToClipboard}
                onOpenDetail={actions.openCustomerDetail}
                onDragStart={actions.onDragStart}
                onDragEnd={actions.onDragEnd}
                onUpdateLeadScore={actions.updateLeadScore}
                registerRef={registerRef}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
